<template>
  <f7-block class="content-devx-margin-block no-margin-horizontal margin-bottom">
    <f7-row>
      <f7-col width="50">
        <div v-if="!loadedGIS || currentIndexes[`${mainMap}`] === ''">
          <div class="loadingFalse">
            <f7-preloader />
          </div>
        </div>
        <div
          v-else-if="gisError"
          class="loadingFalse"
        >
          <p class="gis-unavailable-text">
            {{ $t('Gis.Errors.unavailable') }}
          </p>
        </div>
        <div v-else>
          <GISWMSTimeDimensionMap
            :zoom="zoom"
            :name-map="mainMap"
            :tree-locations="treeLocations"
            :layer="layer"
            :index="currentIndexes[`${mainMap}`]"
            :center="center"
            :bbox="bbox"
            :height="'540px'"
            :companyId="currentPlantation.companyId"
          />
        </div>
      </f7-col>
      <f7-col width="50">
        <GISLayerOptions :key-id="`${mainMap}`" :enable-date-picker="true" />
        <f7-block v-if="!timelineIndexes">
          <div class="loadingFalse">
            <f7-preloader />
          </div>
        </f7-block>

        <f7-row v-else>
          <TimelineGraph
            :timeline-indexes="timelineIndexesFiltered"
            :index="currentIndexes.GisTimeDimension"
            :tasks="tasks"
            :observations="observationsAll"
          />
        </f7-row>
        <f7-row v-if="loadedGIS && excludedDatesLoaded">
          <f7-col width="100">
          <ExclusionDates
            :initial-dates="initialExcludeDates"
            :initial-disabled-dates="disabledDates"
            :min="min"
            :max="max"
            @exclusionDatesChanged="exclusionDatesChanged"
          />
          </f7-col>
        </f7-row>
      </f7-col>
    </f7-row>
  </f7-block>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import GISWMSTimeDimensionMap from '../../../Gis/GISWMSTimeDimensionMap/index.vue';
import GISLayerOptions from '../../../Gis/LayerOptions/index.vue';
import ExclusionDates from '../../../exclusionDates/exclusionDates.vue';
import TimelineGraph from '../timelineGraph/index.vue';
import Api from '../../../../services/Api';
import EventBus from '../../../../js/event-bus';
import {
  buildDisabledDates, getTimelineRange, toDayKey, toLocalDay,
} from './timelineDates';
import { applyWidenedTimelineRange } from '../../../../js/utils/gisDateRange';

export default {
  name: 'GisTimeDimension',
  components: {
    GISWMSTimeDimensionMap,
    GISLayerOptions,
    TimelineGraph,
    ExclusionDates,
  },
  data() {
    return {
      loadedGIS: false,
      gisError: false,
      zoom: 17,
      mainMap: 'GisTimeDimension',
      layer: {
        name: 'gis',
        visible: false,
        timeDimension: true,
        baseUrl: this.$store.getters['Gis/wmsBaseUrl'],
        options: {
          layers: '',
          format: 'image/png',
          transparent: true,
          styles: 'NDVI',
        },
      },
      center: {},
      bbox: {},
      filteredTasks: null,
      ws: Api.getGeoServerWorkspace(),
      initialExcludeDates: [],
      excludedDatesLoaded: false,
      excludedDates: [],
    };
  },
  computed: {
    /**
     * Retorna un array de fechas en formato Date que contiene las fechas,
     * no disponibles, en base a las fechas con imágenes de índices disponibles
     * @return {Array<Date>} Array de fechas en formato Date
     */
    disabledDates() {
      return buildDisabledDates(this.datesList?.avaiable_dates ?? [], this.initialExcludeDates);
    },
    min() {
      if (this.datesList?.avaiable_dates?.length > 0) {
        return toLocalDay(this.datesList.avaiable_dates[0]);
      }
      return undefined;
    },
    max() {
      if (this.datesList?.avaiable_dates?.length > 0) {
        return toLocalDay(this.datesList.avaiable_dates[this.datesList.avaiable_dates.length - 1]);
      }
      return undefined;
    },
    timelineIndexesFiltered() {
      if (this.timelineIndexes) {
        return this.timelineIndexes.filter((timelineIndex) => {
          return !this.excludedDates.includes(timelineIndex.date);
        });
      }
      return [];
    },
    ...mapState('Plantation', ['treeLocations', 'currentPlantation']),
    ...mapState('Gis', ['currentIndexes', 'currentInitIntervalDates', 'currentEndIntervalDates', 'timelineIndexes', 'currentBoundsWMSLayer', 'datesList']),
    ...mapState('TimeDimension', ['tasks', 'observations']),
    ...mapState('TaskByCompany', ['taskActivity']),
    ...mapState('SearcherObservations', { observationsAll: 'observations' })

  },
  async beforeMount() {
    try {
      this.$f7.preloader.show();
      this.setTimelineIndexes(null);
      this.setCurrentIndexes({
        key: this.mainMap,
        newCurrentIndex: 'NDVI',
      });
      this.setCurrentInitIntervalDates({
        key: this.mainMap,
        newCurrentInitIntervalDate: moment().subtract(1, 'month').format('YYYY-MM-DDT00:00:00'),
      });
      this.setCurrentEndIntervalDates({
        key: this.mainMap,
        newCurrentEndIntervalDate: moment().format('YYYY-MM-DDT00:00:00'),
      });
      this.layer.options.layers = `${this.ws}:${this.currentPlantation.sectorId}`;
      this.center = { ...this.currentPlantation.coordinates };
      await this.fetchLocationsByPlantation();
      await this.getObservations({ companyId: this.currentPlantation.companyId, currentPlantationId: this.currentPlantation.id });
      await this.getTasksForActivity();
    } finally {
      // Liberamos el preloader antes de las llamadas a GIS para que el usuario pueda navegar
      this.$f7.preloader.hide();
    }

    try {
      await this.boundsWMSLayer({
        currentOverlayLayer: `timacagro:${this.currentPlantation.sectorId}`,
        companyId: this.currentPlantation.companyId,
      });
      this.bbox = { ...this.currentBoundsWMSLayer.coverage.latLonBoundingBox };
      this.center = { ...this.currentPlantation.coordinates };
    } catch (error) {
      this.gisError = true;
      this.$notifyDX(
        {
          message: this.$t(this.$helpers.getFilteredErrorMessage(`${error.message} wms`)),
          width: 550,
        },
        'warning',
        3000,
      );
    } finally {
      this.loadedGIS = true;
    }

    // Llamadas secundarias que no bloquean el mapa — se ejecutan en paralelo
    const [timelineResult] = await Promise.allSettled([
      this.fetchTimeline(),
      Promise.all([this.getTasks(), this.getObservationsFiltered()]),
      this.loadExcludedDates(),
    ]);

    if (timelineResult.status === 'rejected') {
      this.$notifyDX({
        message: this.$t('downloadReportError'),
        width: 550,
      }, 'error', 7000);
    }

    EventBus.$on('filteringTasks', this.getTasks);
    EventBus.$on('filteringTasks', this.getObservationsFiltered);

    // Después de registrar los listeners: si el rango por defecto se ha ampliado, se
    // recargan las tareas y observaciones con él (evento filteringTasks)
    if (timelineResult.status === 'fulfilled') {
      applyWidenedTimelineRange(this, this.mainMap, timelineResult.value);
    }
  },
  beforeDestroy() {
    EventBus.$off('filteringTasks', this.getTasks);
    EventBus.$off('filteringTasks', this.getObservationsFiltered);
  },
  methods: {
    /**
     * Pide la línea temporal para el rango seleccionado (por defecto el último mes),
     * el mismo que muestra GISLayerOptions; si no tiene imágenes se amplía como el mapa.
     */
    fetchTimeline() {
      return this.fetchTimelineIndexes({
        location: this.currentPlantation.sectorId,
        ...getTimelineRange(this.currentInitIntervalDates[this.mainMap], this.currentEndIntervalDates[this.mainMap]),
        companyId: this.currentPlantation.companyId,
        widenRangeIfEmpty: true,
      });
    },
    /**
     * Carga las fechas excluidas guardadas. ExclusionDates copia sus fechas iniciales al
     * crearse, así que solo se muestra cuando ya están cargadas: si se crease antes, al
     * añadir una fecha se guardaría solo esa y se perderían las anteriores (EDSER-2138).
     * Por lo mismo, si la carga falla (salvo 404: aún no hay ninguna) no se muestra.
     */
    async loadExcludedDates() {
      let datesExcluded;
      try {
        const result = await Api.getElement(
          `/location_gis_dates/${this.currentPlantation.sectorId}/`,
          {},
          this.currentPlantation.companyId,
          15000,
        );
        datesExcluded = JSON.parse(result.response).dates_excluded ?? [];
      } catch (error) {
        if (error?.statusCode !== 404) {
          // Una respuesta que no es JSON no se notifica, como hasta ahora
          if (!(error instanceof SyntaxError)) {
            this.$notifyDX(
              {
                message: this.$t(this.$helpers.getFilteredErrorMessage(`${error?.message}`)),
                width: 550,
              },
              'warning',
              3000,
            );
          }
          return;
        }
        datesExcluded = [];
      }
      // El backend devuelve medianoche UTC ('2026-09-15T00:00:00Z'): el día es el
      // prefijo; moment(date) lo pasaría a local y en UTC negativo daría el día anterior.
      this.initialExcludeDates = datesExcluded.map(toDayKey);
      this.excludedDates = [...this.initialExcludeDates];
      this.excludedDatesLoaded = true;
    },
    async exclusionDatesChanged(dates) {
      this.excludedDates = dates;
      const datesExcluded = this.excludedDates.map((date) => {
        return moment(date).format('YYYY-MM-DDT00:00:00');
      });
      try {
        this.$f7.preloader.show();
        const payload = {
          dates_excluded: datesExcluded,
          location_id: this.currentPlantation.sectorId,
        };
        await Api.createUpdateElement('/location_gis_dates/', payload, this.currentPlantation.companyId, 'POST');
        await this.fetchTimeline();
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(`${error}`),
            width: 550,
          },
          'error',
          3000,
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },
    async getTasks() {
      try {
        this.resetData();

        for (const activity of this.taskActivity) {
          await this.fetchTasks({
            idCompany: this.currentPlantation.companyId,
            idPlantation: this.currentPlantation.id,
            activity,
            initDate: moment(this.currentInitIntervalDates.GisTimeDimension).format('YYYY-MM-DD'),
            endDate: moment(this.currentEndIntervalDates.GisTimeDimension).format('YYYY-MM-DD'),
          });
        }
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(`${error}`),
            width: 550,
          },
          'error',
          3000,
        );
      }
    },
    async getObservationsFiltered() {
      try {
        for (const category of this.observationsAll) {
          await this.fetchObservations({
            idCompany: this.currentPlantation.companyId,
            idPlantation: this.currentPlantation.id,
            category: category.category,
            initDate: moment(this.currentInitIntervalDates.GisTimeDimension).format('YYYY-MM-DD'),
            endDate: moment(this.currentEndIntervalDates.GisTimeDimension).format('YYYY-MM-DD'),
          });
        }
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(`${error}`),
            width: 550,
          },
          'error',
          3000,
        );
      }
    },
    ...mapActions('Plantation', ['fetchLocationsByPlantation']),
    ...mapActions('Gis', ['setCurrentIndexes', 'setCurrentInitIntervalDates', 'setCurrentEndIntervalDates', 'fetchTimelineIndexes', 'setTimelineIndexes', 'boundsWMSLayer']),
    ...mapActions('TimeDimension', ['fetchTasks', 'resetData', 'fetchObservations']),
    ...mapActions('TaskByCompany', ['getTasksForActivity']),
    ...mapActions('SearcherObservations', ['getObservations']),
  },
};
</script>

<style lang="scss" scoped>
@import './GisTimeDimension.styles.scss';
</style>
